import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axiosInstance from "../utils/axiosInstance";
import UseGraph from "../utils/useGraph";
import formQueryUrl from "../utils/formQueryUrl";

const data = [
  { name: "NCB", short: "NCB", type: "ncbScan", value: 500, fill: "#6366F1" },
  { name: "MNCB", short: "MNCB", type: "mirroredNcbProof", value: 300, fill: "#818CF8" },
  { name: "DL. SUMMARY", short: "DL", type: "driversLicenseSummary", value: 200, fill: "#38BDF8" },
  { name: "V5C", short: "V5C", type: "v5cScan", value: 100, fill: "#14B8A6" },
  { name: "PHOTOCARD", short: "PHOTO", type: "photosOfVan", value: 250, fill: "#F59E0B" },
  { name: "PREVIOUS CLAIMS PROOF", short: "CLAIMS", type: "disclosedClaims", value: 150, fill: "#F43F5E" },
  { name: "OTHERS", short: "OTHER", type: "other", value: 350, fill: "#A855F7" },
];

const compact = (value: number) =>
  new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(value);

const ChartTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;

  return (
    <div className="rounded-xl bg-surface-elevated border border-edge shadow-overlay px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-light-gray">{item?.name}</p>
      <p className="text-[15px] font-bold text-text-primary tabular-nums">
        {new Intl.NumberFormat("en-GB").format(item?.value ?? 0)}
      </p>
    </div>
  );
};

export default function BarChartData() {
  const [loading, setLoading] = useState<boolean>(false)
  const [widgetCount, setWidgetCount] = useState<Record<string, any>[]>(data)
  const { startDate, endDate } = UseGraph()



  useEffect(() => {
    const findStatsData = async () => {
      setLoading(true)
      try {
        const filtersArray = [startDate !== null && { startDate }, endDate !== null && { endDate }]
        const widgetData = await axiosInstance.get(`/stats/chase-items/type/counts${formQueryUrl(filtersArray, '?')}`)
        setWidgetCount((prev) => [...prev.map((widgetItem) => {
          if (widgetItem?.type === "other") {
            const selectedItems = data.map((dataItem) => dataItem?.type)

            return {
              ...widgetItem, value: widgetData.data.reduce((prevWidgetData, newWidgetData) => {
                if (!selectedItems.includes(newWidgetData?.type)) {
                  return prevWidgetData += Number(newWidgetData?.count)
                } else {
                  return prevWidgetData
                }
              }, 0)
            }

          } else {
            const currentWidetData = widgetData.data.find((item) => item?.type === widgetItem?.type)
            return { ...widgetItem, value: currentWidetData?.count ? Number(currentWidetData?.count) : 0 }
          }
        })])
        setLoading(false)
      } catch (error) {
        console.log(error)
        setLoading(false)
      }
    }

    findStatsData()
  }, [startDate, endDate])


  if (loading) {
    return (
      <div className="animate-pulse bg-surface border border-edge rounded-2xl h-full min-h-[420px]" />
    )
  }

  const total = widgetCount.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);

  return (
    <div className="rounded-2xl bg-surface border border-edge shadow-card h-full flex flex-col px-6 py-5 animate-fade-in smallTablet:px-4 smallTablet:min-w-0">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[16px] font-bold text-text-primary">Chase Items</h1>
          <p className="mt-0.5 text-[13px] font-medium text-light-gray">Documents chased, by type</p>
        </div>
        <div className="text-right">
          <p className="text-[22px] font-bold text-text-primary tracking-tight leading-7 tabular-nums">
            {compact(total)}
          </p>
          <p className="text-[12px] font-medium text-light-gray">in period</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4">
        {widgetCount.map((entry) => (
          <div key={entry?.type} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry?.fill }}
            />
            <p className="text-[11px] font-medium text-light-gray">{entry?.name}</p>
          </div>
        ))}
      </div>

      <div className="flex-1 min-h-[220px] mt-4 -ml-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={widgetCount}
            margin={{ top: 8, right: 4, left: 0, bottom: 0 }}
            barCategoryGap="35%"
          >
            <CartesianGrid
              vertical={false}
              stroke="var(--color-border-edge-faint)"
            />
            <XAxis
              dataKey="short"
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tick={{ fill: "var(--color-light-gray)", fontSize: 11 }}
            />
            <YAxis
              width={44}
              tickLine={false}
              axisLine={false}
              tickCount={5}
              tickFormatter={compact}
              tick={{ fill: "var(--color-light-gray)", fontSize: 11 }}
            />
            <Tooltip
              cursor={{ fill: "var(--color-bg-overlay)" }}
              content={<ChartTooltip />}
            />
            <Bar dataKey="value" radius={[6, 6, 6, 6]} name="Chases" maxBarSize={38}>
              {widgetCount.map((entry) => (
                <Cell key={entry?.type} fill={entry?.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
